// synced from loomweb blocks/file-edit-block-view.tsx (de-Tailwind'd, read-only).
/**
 * File-edit block. Faithful to loomweb's FileEditBlockView: header (icon +
 * "Editing files" + a Done badge), an optional active-text row, the file list
 * (per-file status icon + monospace name), and a collapsible result panel.
 * Tailwind classes are replaced by namespaced `replay-*` CSS; lucide icons by the
 * local inline-SVG set. loom's clickable "open in code-server" affordance is
 * dropped here (replay is read-only, no live workspace), so files render as
 * static rows — the only behavioral trim from upstream.
 */
import type { JSX } from "react";
import type { FileEditBlock } from "@shared/loom-message-block";
import { FileEditIcon, CheckIcon, LoaderIcon } from "./icons";
import { ToolResultPanel } from "./tool-result-panel";

export function FileEditBlockView({ block }: { block: FileEditBlock }): JSX.Element {
  const isInProgress = block.status === "in_progress" || block.status === "streaming";

  return (
    <div className="replay-block replay-file-edit-block">
      <div className="replay-block-head">
        <FileEditIcon className="replay-icon replay-text-bash" />
        <span className="replay-block-label">Editing files</span>
        {block.status === "completed" && (
          <span className="replay-block-flag replay-text-success">
            <CheckIcon className="replay-icon-xs" />
            Done
          </span>
        )}
      </div>

      {isInProgress && (
        <div className="replay-block-active">
          {block.activeText !== undefined && block.activeText.length > 0
            ? block.activeText
            : `${block.completedFiles}/${block.totalFiles}`}
        </div>
      )}

      {block.files.length > 0 && (
        <div className="replay-file-list">
          {block.files.map((file) => (
            <div key={file.id} className="replay-file-row">
              {file.status === "writing" ? (
                <LoaderIcon className="replay-icon-xs replay-spin replay-text-primary" />
              ) : file.status === "completed" ? (
                <CheckIcon className="replay-icon-xs replay-text-success" />
              ) : (
                <span className="replay-icon-xs" />
              )}
              <span className="replay-file-name">{file.fileName}</span>
            </div>
          ))}
        </div>
      )}

      {block.result?.output !== undefined && block.result.output.length > 0 && (
        <ToolResultPanel
          title="Result"
          content={block.result.output}
          copyAriaLabel="复制编辑结果"
          defaultCollapsed={block.collapsed ?? true}
          status={block.status}
        />
      )}
    </div>
  );
}
